// =======================================
// modules/header/header.js
// ヘッダー制御（トップのヒーロー前提）
// - トップ：常に表示（透明）
// - スクロール中：基本は隠す（没入）
// - ヒーロー下 + 900px 以降：上スクロールで表示 / 下スクロールで非表示（sticky）
// - header 高さを html[data-scroll-offset] / --pb-header-h に反映
// =======================================

import { throttle } from "../../utils/throttle";

const TOP_Y = 20;           // ほぼ最上部判定
const REVEAL_AFTER = 900;   // 「ヒーロー下から○px」以降で sticky を有効化

function syncHeaderHeight(header) {
  const h = header?.offsetHeight || 0;
  document.documentElement.style.setProperty("--pb-header-h", `${h}px`);
  document.documentElement.dataset.scrollOffset = String(h);
}

function getHeroHeight() {
  const hero = document.querySelector(".p-hero");
  if (!hero) return window.innerHeight;
  const rect = hero.getBoundingClientRect();
  return Math.max(0, rect.height || 0) || window.innerHeight;
}

/**
 * @param {HTMLElement} header [data-header]
 * @returns {() => void} cleanup
 */
export function createHeader(header) {
  if (!header) return () => {};
  if (header.dataset.pbHeaderInit === "1") return () => {};
  header.dataset.pbHeaderInit = "1";

  let lastY = window.scrollY || 0;
  let thresholdY = getHeroHeight() + REVEAL_AFTER;

  const show = () => {
    header.classList.add("is-header-shown");
    header.classList.remove("is-header-hidden");
  };

  const hide = () => {
    header.classList.add("is-header-hidden");
    header.classList.remove("is-header-shown");
  };

  const setTopState = (y) => {
    const isTop = y <= TOP_Y;
    header.classList.toggle("is-header-top", isTop);

    if (isTop) {
      header.classList.remove("is-header-sticky");
      show();
    }
    return isTop;
  };

  const onScroll = () => {
    const y = window.scrollY || 0;

    // ① 最上部：必ず表示（透明）
    const isTop = setTopState(y);
    if (isTop) {
      lastY = y;
      return;
    }

    // ② ヒーロー〜ヒーロー下+900px までは「上スクロールでも出さない」
    if (y < thresholdY) {
      header.classList.remove("is-header-sticky");
      hide();
      lastY = y;
      return;
    }

    // ③ しきい値以降：上スクロールで出す / 下スクロールで隠す
    header.classList.add("is-header-sticky");

    if (y < lastY) show();
    else hide();

    lastY = y;
  };

  const onResize = throttle(() => {
    syncHeaderHeight(header);
    thresholdY = getHeroHeight() + REVEAL_AFTER;
    onScroll();
  }, 100);

  const onScrollThrottled = throttle(onScroll, 50);

  // 初期同期
  syncHeaderHeight(header);
  onScroll();

  window.addEventListener("scroll", onScrollThrottled, { passive: true });
  window.addEventListener("resize", onResize);

  return () => {
    window.removeEventListener("scroll", onScrollThrottled);
    window.removeEventListener("resize", onResize);
    delete header.dataset.pbHeaderInit;
  };
}
